// HSSNA JWT 解码 (前端,不验证签名)
// 规格: SPEC §7 安全合规 — JWT RS256 + MFA + RBAC
// 约束: 前端无密钥,仅 base64url 解码 payload,不验证签名
//
// JwtPayload 包含 HSSNA 自定义 claims: roles / family_group_id

/**
 * JWT Payload (仅解析,不验证签名)
 *
 * HSSNA 自定义 claims:
 *   - roles:           角色数组 (RBAC, 见 SPEC §7)
 *   - family_group_id: 家庭组隔离 ID (NF-SEC-04 RLS)
 *   - session_id:      会话 ID (MFA 流程)
 */
export interface JwtPayload {
  readonly sub?: string;
  readonly exp?: number;
  readonly iat?: number;
  readonly iss?: string;
  readonly aud?: string;
  readonly roles?: readonly string[];
  readonly family_group_id?: string;
  readonly session_id?: string;
  readonly [key: string]: unknown;
}

/**
 * 解码 JWT (不验证签名)
 *
 * @param token JWT 字符串 (header.payload.signature)
 * @returns     解码后的 payload;非法 token 返回 null
 */
export function decodeJwt(token: string): JwtPayload | null {
  if (typeof token !== 'string' || token.length === 0) {
    return null;
  }
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }
  const payloadPart = parts[1];
  if (!payloadPart || payloadPart.length === 0) {
    return null;
  }
  try {
    const decoded = base64UrlDecode(payloadPart);
    const parsed = JSON.parse(decoded) as unknown;
    if (parsed === null || typeof parsed !== 'object') {
      return null;
    }
    return parsed as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * 判断 token 是否已过期
 *
 * @param payload JwtPayload (含 exp)
 * @returns       true 表示已过期;无 exp 字段时返回 false (保守策略)
 */
export function isExpired(payload: JwtPayload): boolean {
  if (payload.exp === undefined || typeof payload.exp !== 'number') {
    return false;
  }
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp < nowSeconds;
}

/**
 * 判断 token 是否将在指定秒数内过期
 *
 * @param payload JwtPayload (含 exp)
 * @param seconds 提前量(秒);如 60 表示"60秒内会过期"
 * @returns       true 表示将在 seconds 内过期
 */
export function willExpireWithin(payload: JwtPayload, seconds: number): boolean {
  if (payload.exp === undefined || typeof payload.exp !== 'number') {
    return false;
  }
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp < nowSeconds + seconds;
}

/**
 * base64url 解码为 UTF-8 字符串
 * 兼容浏览器 atob / Node / RN
 */
function base64UrlDecode(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = (4 - (base64.length % 4)) % 4;
  const padded = base64 + '='.repeat(pad);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
}
